import React, { useEffect, useState } from 'react';
import crmClient from './crmClient';
import { stageLabel, STAGE_TONES, formatCurrency, formatNumber } from './format';

const Kpi = ({ label, value, sub, tone }) => (
  <div className={`crm-kpi${tone ? ` crm-kpi--${tone}` : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {sub && <small>{sub}</small>}
  </div>
);

// Convert a preset into a { from, to } ISO range (local time). '' = all time.
const computeRange = preset => {
  const now = new Date();
  const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  switch (preset) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'yesterday': {
      const y = new Date(now);
      y.setDate(now.getDate() - 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case 'this_week': {
      const monday = new Date(now);
      const offset = (now.getDay() + 6) % 7; // Monday-based week start
      monday.setDate(now.getDate() - offset);
      return { from: startOfDay(monday), to: endOfDay(now) };
    }
    case 'this_month':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: endOfDay(now) };
    case 'this_quarter':
      return { from: new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1), to: endOfDay(now) };
    case 'this_year':
      return { from: new Date(now.getFullYear(), 0, 1), to: endOfDay(now) };
    default:
      return { from: null, to: null };
  }
};

const ReportsView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountType, setAccountType] = useState('');
  const [datePreset, setDatePreset] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = {};
    if (accountType) params.account_type = accountType;
    const range = computeRange(datePreset);
    if (range.from) params.date_from = range.from.toISOString();
    if (range.to) params.date_to = range.to.toISOString();
    crmClient
      .reports(params)
      .then(payload => {
        if (active) {
          setData(payload);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setError('Unable to load reports.');
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [accountType, datePreset]);

  if (loading && !data) {
    return <div className="crm-empty">Loading reports…</div>;
  }
  if (error && !data) {
    return <div className="crm-empty crm-empty--error">{error}</div>;
  }

  const totals = (data && data.totals) || {};
  const stages = (data && data.stages) || [];
  const stageCounts = (data && data.stage_counts) || {};
  const email = (data && data.email) || null;
  const maxStage = Math.max(1, ...stages.map(stage => stageCounts[stage] || 0));

  return (
    <div className="crm-reports">
      <div className="crm-filters">
        <label className="crm-date-filter">
          <span>Date range</span>
          <select value={datePreset} onChange={event => setDatePreset(event.target.value)}>
            <option value="">All time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This week</option>
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
        </label>
        <label className="crm-date-filter">
          <span>Segment</span>
          <select value={accountType} onChange={event => setAccountType(event.target.value)}>
            <option value="">All accounts</option>
            <option value="business">Business</option>
            <option value="individual">Individual</option>
          </select>
        </label>
        {loading && <span className="crm-muted">Updating…</span>}
      </div>
      {datePreset && (
        <p className="crm-muted" style={{ margin: '0 0 10px', fontSize: '12px' }}>
          Account &amp; revenue totals below reflect your full current book. The date range scopes the <strong>Email performance</strong> section and the “new in range” counter — lifetime and rolling revenue are not date-filtered.
        </p>
      )}
      <div className="crm-kpi-grid">
        <Kpi
          label="Total accounts"
          value={formatNumber(totals.total_companies)}
          sub={totals.new_accounts_in_range != null ? `${formatNumber(totals.new_accounts_in_range)} new in range` : undefined}
        />
        <Kpi
          label="Active"
          value={formatNumber(totals.active_companies)}
          tone="green"
          sub={`${totals.active_rate_pct || 0}% of accounts`}
        />
        <Kpi
          label="Recurring rate"
          value={`${totals.repeat_rate_pct || 0}%`}
          tone="teal"
          sub={`${formatNumber(totals.repeat_accounts || 0)} of ${formatNumber(totals.ordered_accounts || 0)} ordered 2+`}
        />
        <Kpi label="New accounts · 30d" value={formatNumber(totals.new_accounts_30)} tone="blue" />
        <Kpi
          label="First-order conversion"
          value={`${totals.first_order_conversion_pct || 0}%`}
          sub={`of ${formatNumber(totals.accounts_with_signup_date || 0)} accounts (all time)`}
        />
        <Kpi
          label="First order · 30d"
          value={`${totals.first_order_conversion_30_pct || 0}%`}
          tone="teal"
          sub={`of ${formatNumber(totals.first_order_conversion_30_base || 0)} new (30d)`}
        />
        <Kpi
          label="First order · 90d"
          value={`${totals.first_order_conversion_90_pct || 0}%`}
          tone="teal"
          sub={`of ${formatNumber(totals.first_order_conversion_90_base || 0)} new (90d)`}
        />
        <Kpi label="Reactivations · 30d" value={formatNumber(totals.reactivations_30)} tone="teal" sub="warm/cold → active" />
        <Kpi label="Lost" value={formatNumber(totals.lost_companies)} tone="red" />
        <Kpi label="Lifetime revenue" value={formatCurrency(totals.lifetime_revenue)} />
        <Kpi label="Revenue · 30d" value={formatCurrency(totals.revenue_last_30_days)} />
        <Kpi label="Revenue · 12mo" value={formatCurrency(totals.revenue_last_12_months)} />
      </div>

      {email && (
        <div className="crm-card">
          <h3>Email performance</h3>
          <div className="crm-kpi-grid">
            <Kpi label="Sent" value={formatNumber(email.sent)} sub={`${formatNumber(email.trackable || 0)} with tracking`} />
            <Kpi label="Delivered" value={`${email.delivery_rate_pct || 0}%`} tone="blue" sub={`${formatNumber(email.delivered)} confirmed`} />
            <Kpi label="Open rate" value={`${email.open_rate_pct || 0}%`} tone="green" sub={`${formatNumber(email.opened)} of ${formatNumber(email.trackable || 0)} tracked`} />
            <Kpi label="Click rate" value={`${email.click_rate_pct || 0}%`} tone="teal" sub={`${formatNumber(email.clicked)} of ${formatNumber(email.trackable || 0)} tracked`} />
            <Kpi label="Click-to-open" value={`${email.click_to_open_rate_pct || 0}%`} sub="clicked ÷ opened" />
            <Kpi label="Bounce rate" value={`${email.bounce_rate_pct || 0}%`} tone="red" sub={`${formatNumber(email.bounced)} bounced`} />
            <Kpi label="Unsubscribes" value={formatNumber(email.unsubscribed)} sub={`${email.unsubscribe_rate_pct || 0}% of sent`} />
            <Kpi label="Spam reports" value={formatNumber(email.complained)} tone="red" />
            <Kpi label="Orders from outreach" value={`${email.outreach_conversion_pct || 0}%`} tone="green" sub={`${formatNumber(email.outreach_converted || 0)} of ${formatNumber(email.outreach_recipients || 0)} emailed/texted ordered`} />
          </div>
          {!email.tracking_active && (
            <p className="crm-muted">
              Open and click tracking is built in and records automatically as recipients engage. Rates are measured over sends that
              carry the tracker ({formatNumber(email.trackable || 0)} so far) and populate here after your next send. (Delivery and
              bounce data require the email provider webhook.)
            </p>
          )}
        </div>
      )}

      <div className="crm-card">
        <h3>Accounts by stage</h3>
        <div className="crm-bars">
          {stages.map(stage => {
            const count = stageCounts[stage] || 0;
            const pct = Math.round((count / maxStage) * 100);
            return (
              <div className="crm-bar-row" key={stage}>
                <span className="crm-bar-label">{stageLabel(stage)}</span>
                <span className="crm-bar-track">
                  <span className={`crm-bar-fill crm-bar-fill--${STAGE_TONES[stage] || 'slate'}`} style={{ width: `${pct}%` }} />
                </span>
                <span className="crm-bar-value">{formatNumber(count)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ReportsView;
